import { MetricBenchmarkData, Workout } from '@strength/domain-workout';
import { SingleWorkoutEditContext } from '../../../block/block-all/SingleWorkoutSheet';
import type {
  MiscMutationFns,
  WorkoutLayerMutationFns,
} from '../../../block/mutation-fns/types';
import { WorkoutWithCycleAndSession } from '../../../block/types';

const noop = () => {};

/**
 * Every {@link WorkoutLayerMutationFns} entry as a no-op. The preview drawer is
 * always `forceReadOnly`, so no edit ever reaches these; they exist only to
 * satisfy the (required) editable Sheet surface. Kept inline here in the
 * drawer's own adapter so it can be updated as the drawer gains capabilities.
 */
const previewMutationFns: WorkoutLayerMutationFns = {
  onClearWorkout: noop,
  onAddSection: noop,
  onEditSection: noop,
  onRemoveSection: noop,
  onAddExercise: noop,
  onEditExercise: noop,
  onRemoveExercise: noop,
  onAddSuperset: noop,
  onEditSuperset: noop,
  onRemoveSuperset: noop,
  onAddSupersetExercise: noop,
  onEditSupersetExercise: noop,
  onRemoveSupersetExercise: noop,
  onAddAlternateExercise: noop,
  onEditAlternateExercise: noop,
  onRemoveAlternateExercise: noop,
  onAddAlternateGroup: noop,
  onEditAlternateGroup: noop,
  onRemoveAlternateGroup: noop,
  onAddExerciseMetric: noop,
  onEditExerciseMetric: noop,
  onRemoveExerciseMetric: noop,
  onRemoveExerciseMetrics: noop,
  onSwapExerciseMetric: noop,
  onRemoveSets: noop,
  onAddSet: noop,
  onEditSet: noop,
  onRemoveSet: noop,
  onEditSetMetric: noop,
  onEditSetMetrics: noop,
  onSwapExercise: noop,
  onMoveEntriesIntoNewSection: noop,
};

/** No-op for every required {@link MiscMutationFns} entry (see above). */
const previewMiscMutationFns: MiscMutationFns = {
  onSelectWorkoutTemplate: noop,
  onSelectScheme: noop,
};

/**
 * Raw workout status on `block.getWorkoutPreview`'s `blockLayer` row
 * (`BlockPublishedWorkoutLayer.status`). Mirrors the `workout_status` pg enum,
 * kept as a literal union here instead of importing `@strength/db-schema`
 * into the web app.
 */
export type PreviewWorkoutStatus =
  | 'planned'
  | 'completed'
  | 'canceled'
  | 'skipped';

/**
 * Block-layer metadata `SingleWorkoutEditContext`/`WorkoutWithCycleAndSession`
 * need that lives on the raw `blockLayer` row, not on the merged `Workout`
 * (SC-4019 contract correction). `getWorkoutPreview` does not return the
 * parent Block's own `BlockStatus` (planned/canceled), only this workout's
 * `status`, so the read-only edit context's `blockStatus` defaults to
 * `'planned'` in {@link buildPreviewEditContext} rather than fabricating it.
 */
export type WorkoutPreviewBlockMeta = {
  cycleSortOrder: number;
  sessionSortOrder: number;
  status: PreviewWorkoutStatus;
};

function toDisplayWorkoutStatus(
  status: PreviewWorkoutStatus,
): WorkoutWithCycleAndSession['status'] {
  switch (status) {
    case 'canceled':
      return { type: 'canceled' };
    case 'skipped':
      return { type: 'skipped' };
    case 'completed':
      return { type: 'completed', completed: 1, toComplete: 1, readOnly: true };
    case 'planned':
      return { type: 'planned' };
  }
}

/**
 * Converts the client-side merged `Workout` (via `mergeWorkoutLayers`) plus
 * the block-layer metadata into the `WorkoutWithCycleAndSession` shape
 * `SingleWorkoutSheet` renders.
 *
 * `cycle`/`session` carry only the sort-order `id`s the Sheet needs to scope
 * its `cycle` view and lay out grid columns; their `name`/date-range fields
 * are never rendered here (`SingleWorkoutSheet` hardcodes `hideWorkoutLabel:
 * true` for every caller, which hides the only slot that reads them), so
 * they're filled with safe, non-crashing placeholders rather than fetching
 * the full block/session labels this endpoint doesn't return.
 */
export function toPreviewWorkout(
  workout: Workout,
  blockMeta: WorkoutPreviewBlockMeta,
): WorkoutWithCycleAndSession {
  return {
    ...workout,
    exists: true,
    isAthleteOnlyLayer: false,
    actual: undefined,
    modifiedPublished: undefined,
    status: toDisplayWorkoutStatus(blockMeta.status),
    cycle: {
      id: blockMeta.cycleSortOrder,
      name: '',
      startDate: workout.assignedDate ?? '',
      endDate: workout.assignedDate ?? '',
    },
    session: {
      id: blockMeta.sessionSortOrder,
      name: '',
    },
  };
}

/**
 * Read-only `SingleWorkoutEditContext` for the Calendar preview drawer.
 * `forceReadOnly: true` disables every interactive Sheet affordance (add /
 * remove, context menu, drag & drop, date/name editing), so every mutation
 * handler below is a genuine no-op supplied by this drawer's own adapter
 * ({@link previewMutationFns}/{@link previewMiscMutationFns} and the inline
 * callbacks) rather than being wired to a real draft-layer mutation.
 */
export function buildPreviewEditContext(
  applyBenchmarkData: MetricBenchmarkData,
  profileId: string | null,
): SingleWorkoutEditContext {
  return {
    allErrors: [],
    applyBenchmarkData,
    hasBlockBeenPublished: true,
    isPublishDisabled: true,
    blockStatus: 'planned',
    allowFlexWorkouts: false,
    selectedAthleteId: profileId ?? undefined,
    showActuals: false,
    forceReadOnly: true,
    getAssignedDate: (workout) => workout.assignedDate,
    mutationFns: previewMutationFns,
    miscMutationFns: previewMiscMutationFns,
    onUndo: noop,
    onRedo: noop,
    onCopy: noop,
    onPaste: noop,
    onBulkClear: noop,
    onDragEnd: noop,
  };
}
